import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import LoadingSpinner from '../LoadingSpinner';
import './ItemAdmin.css';

function ItemAdmin() {
  const { sku } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [inventory, setInventory] = useState([]);
  const [totalQuantity, setTotalQuantity] = useState(0);
  const [poData, setPoData] = useState({ onOrderQty: 0, earliestArrival: null });
  const [woData, setWoData] = useState({ onWorkOrderQty: 0 });
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState(false);
  const [authenticatedUser, setAuthenticatedUser] = useState(null);

  // Check if item is an assembly type
  const isAssembly = item?.type === 'Assembly';

  useEffect(() => {
    // Check if already authenticated in session
    const authenticated = sessionStorage.getItem('ops_authenticated');
    const user = sessionStorage.getItem('ops_user');
    if (authenticated === 'true' && user) {
      setIsAuthenticated(true);
      setAuthenticatedUser(JSON.parse(user));
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [sku, isAuthenticated]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch item, inventory, PO data, and WO data in parallel
      const [itemResponse, inventoryResponse, poResponse, woResponse] = await Promise.all([
        axios.get(`/api/item/${sku}`),
        axios.get(`/api/ops/inventory/${sku}`),
        axios.get(`/api/ops/item/${sku}/purchase-orders`).catch(() => ({ data: { onOrderQty: 0, earliestArrival: null } })),
        axios.get(`/api/ops/item/${sku}/work-orders`).catch(() => ({ data: { onWorkOrderQty: 0 } }))
      ]);

      setItem(itemResponse.data);
      setInventory(inventoryResponse.data);
      setPoData(poResponse.data);
      setWoData(woResponse.data);

      const total = inventoryResponse.data.reduce((sum, inv) => sum + inv.quantity, 0);
      setTotalQuantity(total);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const verifyPin = async (pin) => {
    try {
      const response = await axios.post('/api/operations-users/verify', { pin });
      if (response.data.valid) {
        setIsAuthenticated(true);
        setAuthenticatedUser(response.data.user);
        sessionStorage.setItem('ops_authenticated', 'true');
        sessionStorage.setItem('ops_user', JSON.stringify(response.data.user));
        setPinError(false);
        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.error('PIN verification failed:', error);
      return false;
    }
  };

  const handlePinChange = async (index, value) => {
    if (!/^\d*$/.test(value)) return; // Only allow digits

    const newPin = pinInput.split('');
    newPin[index] = value;
    const updatedPin = newPin.join('');
    setPinInput(updatedPin);
    setPinError(false);

    // Move to next input or auto-submit
    if (value && index < 3) {
      const nextInput = document.getElementById(`pin-${index + 1}`);
      if (nextInput) nextInput.focus();
    } else if (updatedPin.length === 4) {
      // Auto-submit when 4 digits entered
      const isValid = await verifyPin(updatedPin);
      if (!isValid) {
        setPinError(true);
        setTimeout(() => {
          setPinInput('');
          document.getElementById('pin-0')?.focus();
        }, 500);
      }
    }
  };

  const handlePinKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const newPin = pinInput.split('');
      if (newPin[index]) {
        newPin[index] = '';
        setPinInput(newPin.join(''));
      } else if (index > 0) {
        newPin[index - 1] = '';
        setPinInput(newPin.join(''));
        document.getElementById(`pin-${index - 1}`)?.focus();
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (!isAuthenticated) {
    return (
      <div className="item-admin-container">
        <div className="pin-lock">
          <div className="pin-lock-header">
            <h1>Operations Access</h1>
            <p>Enter PIN to continue</p>
          </div>
          <div className={`pin-dots ${pinError ? 'pin-error' : ''}`}>
            {[0, 1, 2, 3].map((index) => (
              <input
                key={index}
                id={`pin-${index}`}
                type="tel"
                inputMode="numeric"
                maxLength="1"
                value={pinInput[index] || ''}
                onChange={(e) => handlePinChange(index, e.target.value)}
                onKeyDown={(e) => handlePinKeyDown(index, e)}
                className="pin-dot"
                autoFocus={index === 0}
              />
            ))}
          </div>
          {pinError && <div className="error-message">Incorrect PIN</div>}
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="item-admin-container">
        <LoadingSpinner />
      </div>
    );
  }

  if (!item) {
    return (
      <div className="item-admin-container">
        <div className="item-admin-header">
          <button className="back-button" onClick={() => navigate('/ops')}>
            Back
          </button>
          <h1>Item Admin</h1>
        </div>
        <div className="error-card">Item not found: {sku}</div>
      </div>
    );
  }

  return (
    <div className="item-admin-container">
      {/* Header */}
      <div className="item-admin-header">
        <button className="back-button" onClick={() => navigate('/ops')}>
          Back
        </button>
        <h1>Item Admin</h1>
      </div>

      {/* Item Info Card */}
      <div className="item-info-card">
        <div className="item-sku">SKU: {sku}</div>
        <div className="item-description">{item.description}</div>
        <div className="item-type">Type: {item.type || 'N/A'}</div>
      </div>

      {/* Summary Stats Card */}
      <div className="summary-card">
        <div className="summary-row">
          <span className="summary-label">On-Hand Inventory</span>
          <span className="summary-value">{totalQuantity}</span>
        </div>
        <div className="summary-row">
          <span className="summary-label">On Purchase Orders</span>
          <span className="summary-value">{poData.onOrderQty || 0}</span>
        </div>
        {isAssembly && (
          <div className="summary-row">
            <span className="summary-label">On Work Orders</span>
            <span className="summary-value">{woData.onWorkOrderQty || 0}</span>
          </div>
        )}
        <div className="summary-row">
          <span className="summary-label">Est. Arrival</span>
          <span className="summary-value">{formatDate(poData.earliestArrival)}</span>
        </div>
      </div>

      {/* Inventory by Location Card */}
      {inventory.length > 0 && (
        <div className="inventory-card">
          <div className="inventory-card-header">Inventory by Location</div>
          <div className="inventory-list">
            {inventory.map((inv, index) => (
              <div key={index} className="inventory-row">
                <span className="bin-code">{inv.bin_code}</span>
                <span className="bin-qty">{inv.quantity} units</span>
                <Link
                  to={`/ops/${sku}/transfer?fromBin=${inv.bin_code}`}
                  className="transfer-link"
                >
                  Transfer
                </Link>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="action-buttons">
        <Link to={`/ops/${sku}/transfer`} className="action-button">
          <div className="action-label">Bin Transfer</div>
          <div className="action-desc">Move items between bins</div>
        </Link>

        <Link to={`/ops/${sku}/putaway`} className="action-button">
          <div className="action-label">Inventory Adjustment</div>
          <div className="action-desc">Add, remove, or count inventory</div>
        </Link>

        {isAssembly && (
          <>
            <Link to={`/ops/${sku}/convert`} className="action-button">
              <div className="action-label">Cases to Singles</div>
              <div className="action-desc">Convert cases to singles</div>
            </Link>

            <Link to={`/ops/${sku}/singles-convert`} className="action-button">
              <div className="action-label">Singles to Cases</div>
              <div className="action-desc">Convert singles to cases</div>
            </Link>
          </>
        )}
      </div>
    </div>
  );
}

export default ItemAdmin;
